import PropTypes from 'prop-types';
import React, { Fragment, memo, useCallback, useEffect, useLayoutEffect, useMemo, useState, useContext } from 'react';
import _ from 'underscore';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Constants, TaxUtils } from '@bingads-webui-clientcenter/entity-utils';
import { LineByLineFormGroup } from '@bingads-webui-cc-react/layout';
import { Currency } from '@bingads-webui-react/currency';
import { getUrlBuilders } from '@bingads-webui-clientcenter/url-builder-base';
import { PaymentContext } from '@bingads-webui-cc-react/payment-context';

import { AmountInput } from './amount-input';
import { Challenger } from './challenger';
import { PropTypeTopUpLimit, PropTypesPaymentProps, PropTypeSignupAddFundsResult } from './shapes';
import { OfflinePaymentMethodSection } from './offline-paymentmethod-section';
import { AutoRechargeForm } from './auto-recharge';
import { baseTaxingAmount } from './constants';
import { StyledToggle } from './styled-toggle';

const { PaymentInstrumentType } = Constants;
const { getTaxRate, getTaxAndBalance } = TaxUtils;

const isCreditCard = piType => piType === PaymentInstrumentType.CreditCard;
const isOfflinePaymentMethod = piType => piType === PaymentInstrumentType.OfflinePaymentMethod;
const isUnsetPiType = piType => piType === PaymentInstrumentType.Unset;

const topUpReducer = (mem, { piType, limit }) => ({
  ...mem,
  [piType]: limit,
});

const SignupAddFundsFormUnWrapped = ({
  aid,
  cid,
  fetchOfflinePiInstructions,
  accountNumber,
  currency,
  EstimatedTaxAmount,
  piType,
  piCountry,
  TopUpLimits,
  SignupAddFundsResult,
  setIsValid,
  paymentProps,
  permissions,
  isAutoRechargeOn,
  setIsAutoRechargeOn,
  paymentAmount,
  setPaymentAmount,
  isSmartAddFundsUrl,
  alwaysShowAddFundsAmountError,
  shouldRetryWithPopup,
  shouldAuthenticateWithPopup,
}) => {
  const i18n = useContext(I18nContext);
  const {
    paymentContext: {
      AuthType,
    },
  } = useContext(PaymentContext);
  const isPAAS = ['Google', 'Facebook'].includes(AuthType);
  const isGoogleAuthIntlNewSignInPageEnabled = _.get(permissions, ['dynamic', 'GoogleAuthIntlNewSignInPage'], false);

  // if the first attempt to add funds fails, we will allow the user to skip adding funds
  const [hasFailed, setHasFailed] = useState(false);
  const [shouldShowAddFunds, setShouldShowAddFunds] = useState(true);

  const [submittingPiType, setSubmittingPiType] = useState(null);
  const { ccBaseUrl, restfulService, isPsd2ValidationEnabled } = paymentProps;
  const estimatedTaxRate = getTaxRate({ tax: EstimatedTaxAmount, taxingAmount: baseTaxingAmount });
  const challengeRequired = isCreditCard(piType) &&
    shouldShowAddFunds &&
    ((!isPAAS && isPsd2ValidationEnabled) ||
     (isPAAS && isGoogleAuthIntlNewSignInPageEnabled));
  const {
    submitting,
    success,
    fail,
    cancel,
    CtpPiId,
    MtPiId,
  } = SignupAddFundsResult;

  const topUpLimitMapping = useMemo(
    () => _.reduce(TopUpLimits, topUpReducer, {}),
    [TopUpLimits]
  );
  const { Max1stPaymentAmount: max, MinPaymentAmount: min } = topUpLimitMapping[piType] || {};

  const { taxAmount, appliedAmount } = getTaxAndBalance({ fundingAmount: paymentAmount, estimatedTaxRate });

  const showTaxes = estimatedTaxRate > 0;
  const isOfflinePi = isOfflinePaymentMethod(piType);

  const addFundsUrl = useMemo(
    () => {
      const urlBuilders = getUrlBuilders({
        ccBaseUrl,
        cidFetcher: _.constant(cid),
        aidFetcher: _.constant(aid),
        getDefaultIsShortUrl: _.constant(false),
      });
      const actionName = isSmartAddFundsUrl ? 'smartprepay' : 'prepay';
      return new urlBuilders.PaymentUrlBuilder().action(actionName).url;
    },
    [aid, ccBaseUrl, cid, isSmartAddFundsUrl]
  );

  const addFunds = useCallback(
    ({
      sessionId,
    }) => restfulService.post(addFundsUrl, {
      switchOnAutoRecharge: isAutoRechargeOn,
      sessionId,
      PaymentModel: {
        CustomerId: cid,
        AccountId: aid,
        PaymentInstrumentId: MtPiId,
        Amount: paymentAmount,
        // Still pass IsRecurring because it is required field even through we never use it
        IsRecurring: isAutoRechargeOn,
        Timestamp: null,
      },
    }).then(success)
      .tapCatch(() => setHasFailed(true))
      .catch(fail),
    [MtPiId, addFundsUrl, aid, cid, fail, isAutoRechargeOn, paymentAmount, restfulService, success]
  );

  // this helps syncronize effects so submit doesn't happen if we change piType while submitting
  useLayoutEffect(
    () => {
      if (submitting) {
        if (submittingPiType === null) {
          setSubmittingPiType(piType);
        }
      } else {
        setSubmittingPiType(null);
      }
    },
    [piType, setSubmittingPiType, submitting, submittingPiType]
  );

  // on values update, set isValid, and initial state
  useEffect(
    () => {
      setIsValid(isOfflinePi || !shouldShowAddFunds || (!isUnsetPiType(piType) && paymentAmount >= min && paymentAmount <= max));
      // cancel submit because piType changed
      if (submitting) {
        cancel();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [piType, setIsValid, isOfflinePi, paymentAmount, min, max, shouldShowAddFunds]
  );

  useLayoutEffect(
    () => {
      if (submitting && submittingPiType === piType && !challengeRequired) {
        if (isOfflinePi || !shouldShowAddFunds) {
          success();
        } else {
          addFunds({
            isAutoRechargeOn,
            paymentAmount,
          });
        }
      }
    },
    [addFunds, challengeRequired, isAutoRechargeOn, isOfflinePi, paymentAmount, piType, shouldShowAddFunds, submitting, submittingPiType, success]
  );

  if (isUnsetPiType(piType)) {
    return null;
  }

  return (
    <div className="addFundsForm">
      {isOfflinePi && (
        <Fragment>
          <OfflinePaymentMethodSection
            i18n={i18n}
            currencyCode={currency}
            countryCode={piCountry}
            fetchOfflinePiInstructions={fetchOfflinePiInstructions}
            accountNumber={accountNumber}
          />
        </Fragment>
      )}
      {
        // if we have already failed the first adds funds request then allow the user to skip
        // don't show for offline pi, as they don't actually add funds now
        !isOfflinePi && hasFailed && (
          <div className="shouldAddFunds">
            <LineByLineFormGroup label={i18n.getString(_TL_('Add funds'))} >
              <StyledToggle
                isChecked={shouldShowAddFunds}
                setIsChecked={setShouldShowAddFunds}
                i18n={i18n}
              />
            </LineByLineFormGroup>
          </div>
        )
      }
      {
        shouldShowAddFunds && (
          <Fragment>
            {
              // don't show the input when offline PI, unless we are showing calculated tax
              (!isOfflinePi || showTaxes) &&
              <div className="addFundsAmount">
                <AmountInput
                  paymentAmount={paymentAmount}
                  setPaymentAmount={setPaymentAmount}
                  max={max}
                  min={min}
                  i18n={i18n}
                  required={!isOfflinePi}
                  alwaysShowError={alwaysShowAddFundsAmountError}
                />
              </div>
            }
            {/* TODO: show the coupon info  */}
            {/* TODO: boleto tax widget? not sure if required (I assume we don't need it because tax is handled on previous step) */}
            {/* Boleto also needs the payment slip code moved to SPA for this */}
            {
              // Estimated Tax and Estimated Payment Applied
              showTaxes && (
              <div className="addFundsTaxEstimation">
                <LineByLineFormGroup label={i18n.getString(_TL_('Estimated taxes'))} >
                  <Currency i18n={i18n} currencyCode={currency} amount={taxAmount} />
                </LineByLineFormGroup>
                <LineByLineFormGroup label={i18n.getString(_TL_('Estimated payment applied'))} >
                  <Currency i18n={i18n} currencyCode={currency} amount={appliedAmount} />
                </LineByLineFormGroup>
              </div>
            )}
            <AutoRechargeForm
              piType={piType}
              paymentAmount={paymentAmount}
              i18n={i18n}
              currency={currency}
              isAutoRechargeOn={isAutoRechargeOn}
              setIsAutoRechargeOn={setIsAutoRechargeOn}
            />

            {challengeRequired
            && submitting
            && CtpPiId
            && MtPiId
            && (
              <Challenger
                isPAAS={isPAAS}
                aid={aid}
                cid={cid}
                i18n={i18n}
                paymentAmount={paymentAmount}
                currency={currency}
                piCountry={piCountry}
                CtpPiId={CtpPiId}
                MtPiId={MtPiId}
                success={addFunds}
                fail={fail}
                cancel={cancel}
                paymentProps={paymentProps}
                shouldRetryWithPopup={shouldRetryWithPopup}
                shouldAuthenticateWithPopup={shouldAuthenticateWithPopup}
              />
            )}
          </Fragment>
        )
      }
    </div>
  );
};

SignupAddFundsFormUnWrapped.propTypes = {
  aid: PropTypes.number.isRequired,
  cid: PropTypes.number.isRequired,
  EstimatedTaxAmount: PropTypes.number.isRequired,
  piType: PropTypes.number.isRequired,
  accountNumber: PropTypes.string.isRequired,
  piCountry: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  TopUpLimits: PropTypes.arrayOf(PropTypeTopUpLimit).isRequired,
  fetchOfflinePiInstructions: PropTypes.func.isRequired,
  SignupAddFundsResult: PropTypeSignupAddFundsResult.isRequired,
  setIsValid: PropTypes.func.isRequired,
  paymentProps: PropTypesPaymentProps.isRequired,
  isAutoRechargeOn: PropTypes.bool,
  setIsAutoRechargeOn: PropTypes.func.isRequired,
  paymentAmount: PropTypes.number,
  setPaymentAmount: PropTypes.func.isRequired,
  isSmartAddFundsUrl: PropTypes.bool,
  alwaysShowAddFundsAmountError: PropTypes.bool,
  permissions: PropTypes.shape({}),
  shouldRetryWithPopup: PropTypes.bool,
  shouldAuthenticateWithPopup: PropTypes.bool,
};

SignupAddFundsFormUnWrapped.defaultProps = {
  paymentAmount: null,
  isSmartAddFundsUrl: true,
  alwaysShowAddFundsAmountError: false,
  permissions: {},
  isAutoRechargeOn: false,
  shouldRetryWithPopup: false,
  shouldAuthenticateWithPopup: false,
};

SignupAddFundsFormUnWrapped.displayName = 'SignupAddFundsForm';

export const SignupAddFundsForm = memo(SignupAddFundsFormUnWrapped);
